/**
 * EditorView — main editor layout: sidebar + mockup grid + transform panel.
 * Replaces window.EditorView.
 */
import { useState, useEffect, useCallback, useRef, useMemo, type FC } from 'react';
import { Icon } from './Icon';
import { PrintCollection } from './PrintCollection';
import { Sidebar } from './Sidebar';
import { MockupGridItem } from './MockupGridItem';
import { TransformPanel } from './TransformPanel';
import type { Product, FileItem, PrintItem } from '../stores/appStore';
import type { Transform } from '../stores/types';

interface Props {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  products: Product[];
  files: FileItem[];
  transforms: Record<string, Transform>;
  productTransforms: Record<string, Transform>;
  setTransforms: React.Dispatch<React.SetStateAction<Record<string, Transform>>>;
  setProductTransforms: React.Dispatch<React.SetStateAction<Record<string, Transform>>>;
  activeProductId: string | null;
  setActiveProductId: (id: string | null) => void;
  selectedPrint: PrintItem | null;
  selectedPrintIds: string[];
  printCollection: PrintItem[];
  mockupsPerRow: number;
  setMockupsPerRow: (n: number) => void;
  isUploading: boolean;
  isExporting: boolean;
  isCloudSaving: boolean;
  saveStatus: 'saved' | 'saving' | 'error';
  maskColor: string | null;
  setMaskColor: (c: string | null) => void;
  presets: Record<string, unknown>;
  auth: { password: string };
  onUploadFiles: (files: FileList) => void;
  onSelectPrint: (file: FileItem) => void;
  onAddPrintToCollection: (file: FileItem) => void;
  onSelectPrintInCollection: (id: string) => void;
  onRemovePrintFromCollection: (id: string) => void;
  onUpdateArticle: (id: string, article: string) => void;
  onUpdatePrintName: (id: string, name: string) => void;
  onSaveCollectionToCloud: (ids: string[]) => void;
  onSavePreset: (name: string, scope: string) => void;
  onDeletePreset: (name: string) => void;
  onApplyPreset: (name: string, applyToAll?: boolean) => void;
  onForceLoadConfig?: () => void;
  onSaveConfig: (products: Product[]) => void;
  onExportZip: () => void;
  onSaveToCloud: () => void;
  onAddProduct: () => void;
  onUpdateProductDPI: (id: string, dpi: number) => void;
  triggerSaveConfig: (
    name: string,
    data: { transforms: Record<string, Transform>; productTransforms: Record<string, Transform> },
  ) => void;
  updatePositions: (id: string, positions: Record<string, Transform>) => void;
}

export const EditorView: FC<Props> = (props) => {
  const {
    activeTab,
    setActiveTab,
    products,
    files,
    transforms,
    productTransforms,
    setTransforms,
    setProductTransforms,
    activeProductId,
    setActiveProductId,
    selectedPrint,
    selectedPrintIds,
    printCollection,
    mockupsPerRow,
    setMockupsPerRow,
    isExporting,
    isCloudSaving,
    saveStatus,
    maskColor,
    setMaskColor,
    presets,
    auth,
    onUploadFiles,
    onAddPrintToCollection,
    onSelectPrintInCollection,
    onRemovePrintFromCollection,
    onUpdateArticle,
    onUpdatePrintName,
    onSaveCollectionToCloud,
    onSavePreset,
    onDeletePreset,
    onApplyPreset,
    onSaveConfig,
    onExportZip,
    onSaveToCloud,
    onAddProduct,
    onUpdateProductDPI,
    triggerSaveConfig,
    updatePositions,
  } = props;

  const isProductsTab = activeTab === 'products';
  const currentTransforms = isProductsTab ? productTransforms : transforms;
  const currentTabProducts = products.filter((p) => p.tab === activeTab);

  // Print picker modal
  const [showPrintPicker, setShowPrintPicker] = useState(false);
  const [pickerSelected, setPickerSelected] = useState<Set<string>>(new Set());
  const [pickerPage, setPickerPage] = useState(1);
  const PRINTS_PER_PAGE = 30;

  const collectionBaseNames = useMemo(() => {
    const s = new Set<string>();
    printCollection?.forEach((p) => {
      s.add(p.name);
      s.add(p.name.split('/').pop()!);
    });
    return s;
  }, [printCollection]);

  const uploadFiles = useMemo(
    () =>
      files
        .filter((f) => f.type === 'upload' || f.type === 'linked')
        .sort((a, b) => (b.mtime || 0) - (a.mtime || 0)),
    [files],
  );
  const pickerTotalPages = Math.max(1, Math.ceil(uploadFiles.length / PRINTS_PER_PAGE));
  const pickerFiles = uploadFiles.slice(
    (pickerPage - 1) * PRINTS_PER_PAGE,
    pickerPage * PRINTS_PER_PAGE,
  );

  const togglePickerSelect = (file: FileItem) => {
    const base = file.name.split('/').pop()!;
    if (collectionBaseNames.has(file.name) || collectionBaseNames.has(base)) return;
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(file.name)) next.delete(file.name);
      else next.add(file.name);
      return next;
    });
  };

  const handlePickerAdd = () => {
    uploadFiles.filter((f) => pickerSelected.has(f.name)).forEach((f) => onAddPrintToCollection(f));
    setPickerSelected(new Set());
    setShowPrintPicker(false);
  };

  const openPrintPicker = () => {
    setPickerSelected(new Set());
    setPickerPage(1);
    setShowPrintPicker(true);
  };

  const updateTransform = (id: string, newT: Transform) => {
    const nextTransforms = { ...currentTransforms, [id]: newT };
    if (isProductsTab) setProductTransforms((prev) => ({ ...prev, [id]: newT }));
    else setTransforms((prev) => ({ ...prev, [id]: newT }));

    // Propagate to linked mockups
    const product = products.find((p) => p.id === id);
    if (product?.linkedMockups?.length) {
      for (const linkedId of product.linkedMockups) {
        if (linkedId === id) continue;
        nextTransforms[linkedId] = { ...newT };
        if (isProductsTab) setProductTransforms((prev) => ({ ...prev, [linkedId]: { ...newT } }));
        else setTransforms((prev) => ({ ...prev, [linkedId]: { ...newT } }));
      }
    }

    if (selectedPrint?.id) {
      updatePositions(selectedPrint.id, {
        ...(selectedPrint.positions || {}),
        [id]: newT,
        ...(product?.linkedMockups || []).reduce((acc, lid) => {
          if (lid !== id) acc[lid] = { ...newT };
          return acc;
        }, {} as Record<string, Transform>),
      } as Record<string, Transform>);
    }
    if (selectedPrint) {
      const fullT = isProductsTab ? transforms : nextTransforms;
      const fullPT = isProductsTab ? nextTransforms : productTransforms;
      triggerSaveConfig(selectedPrint.name, { transforms: fullT, productTransforms: fullPT });
    }
  };

  // ── Ctrl+C / Ctrl+V hotkeys for transform presets ──
  const clipboardRef = useRef<Transform | null>(null);
  const activeIdRef = useRef(activeProductId);
  const transformsRef = useRef(currentTransforms);
  const updateTransformRef = useRef(updateTransform);
  activeIdRef.current = activeProductId;
  transformsRef.current = currentTransforms;
  updateTransformRef.current = updateTransform;

  const [copyToast, setCopyToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const showToast = useCallback((msg: string) => {
    setCopyToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setCopyToast(null), 1800);
  }, []);
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const copyTransformInternal = useCallback(() => {
    const aid = activeIdRef.current;
    if (!aid) {
      showToastRef.current('Выберите мокап');
      return;
    }
    const t = transformsRef.current[aid];
    if (!t) {
      showToastRef.current('Нет трансформации для копирования');
      return;
    }
    clipboardRef.current = { ...t };
    showToastRef.current('Трансформация скопирована');
  }, []);

  const pasteTransformInternal = useCallback(() => {
    const aid = activeIdRef.current;
    if (!aid) {
      showToastRef.current('Выберите мокап');
      return;
    }
    if (!clipboardRef.current) {
      showToastRef.current('Буфер трансформации пуст');
      return;
    }
    updateTransformRef.current(aid, { ...clipboardRef.current });
    showToastRef.current('Трансформация вставлена');
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCtrl = e.ctrlKey || e.metaKey;
      const isCopyHotkey = isCtrl && e.code === 'KeyC';
      const isPasteHotkey = isCtrl && e.code === 'KeyV';
      const isHotkey = isCopyHotkey || isPasteHotkey;

      // Don't intercept when typing in inputs — but ALLOW Ctrl+C / Ctrl+V hotkeys
      const tag = (e.target as HTMLElement)?.tagName;
      if (!isHotkey && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')) return;
      const isEditable = (e.target as HTMLElement)?.isContentEditable;
      if (!isHotkey && isEditable) return;

      // For number inputs (transform sliders), always handle our hotkey
      // For text inputs where user might want native copy/paste, skip
      if (isHotkey && (tag === 'INPUT' || tag === 'TEXTAREA')) {
        const inputEl = e.target as HTMLInputElement;
        const inputType = inputEl.type?.toLowerCase();
        if (inputType !== 'number' && inputType !== 'range') return;
      }

      if (isCopyHotkey) {
        e.preventDefault();
        copyTransformInternal();
      }

      if (isPasteHotkey) {
        e.preventDefault();
        pasteTransformInternal();
      }
    };

    // Используем capture: true — хендлер срабатывает на фазе захвата (первым),
    // до того как любой другой элемент может перехватить событие
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [copyTransformInternal, pasteTransformInternal]);

  const handleSaveTabConfig = (newTabProds: Product[]) => {
    const otherProds = products.filter((p) => p.tab !== activeTab);
    onSaveConfig([...otherProds, ...newTabProds]);
  };

  return (
    <div className="responsive-layout flex flex-col lg:flex-row gap-6 lg:h-[calc(100vh-160px)] fade-in">
      {/* LEFT SIDEBAR */}
      <div className="responsive-sidebar w-full lg:w-72 xl:w-80 flex flex-col gap-4 lg:h-full overflow-y-auto custom-scroll pr-1 shrink-0 pb-24">
        {/* Print picker button */}
        <button
          onClick={openPrintPicker}
          className="w-full glass-card rounded-xl p-3 flex items-center justify-center gap-3 hover:border-indigo-500/50 hover:bg-slate-800/60 transition-all group cursor-pointer"
        >
          <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30 group-hover:bg-indigo-500/30 transition-colors">
            <Icon name="image-plus" className="w-4 h-4 text-indigo-400" />
          </div>
          <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
            Выбрать принты
          </span>
          <span className="text-xs text-slate-500 ml-auto">{uploadFiles.length} файлов</span>
        </button>

        {/* Print picker modal */}
        {showPrintPicker && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowPrintPicker(false)}
          >
            <div
              className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-[90vw] max-w-3xl max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-semibold text-white">Выберите принты</h3>
                  {pickerSelected.size > 0 && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-1 rounded-lg font-medium">
                      Выбрано: {pickerSelected.size}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium cursor-pointer transition-colors border border-slate-700">
                    <Icon name="upload" className="w-3.5 h-3.5" />
                    <span>Загрузить</span>
                    <input type="file" multiple className="hidden" onChange={(e) => { if (e.target.files) onUploadFiles(e.target.files); e.target.value = ''; }} />
                  </label>
                  <button
                    onClick={() => setShowPrintPicker(false)}
                    className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <Icon name="x" className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 custom-scroll">
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {pickerFiles.map((f) => {
                    const isSelected = pickerSelected.has(f.name);
                    const fBase = f.name.split('/').pop()!;
                    const isInCollection =
                      collectionBaseNames.has(f.name) || collectionBaseNames.has(fBase);
                    return (
                      <div
                        key={f.name}
                        onClick={() => togglePickerSelect(f)}
                        className={`aspect-square rounded-lg border-2 overflow-hidden bg-slate-800 relative transition-all ${
                          isInCollection
                            ? 'border-blue-500/50 opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'border-emerald-500 ring-2 ring-emerald-500/30 shadow-lg cursor-pointer'
                              : 'border-slate-700 hover:border-slate-500 cursor-pointer group'
                        }`}
                      >
                        <img
                          src={f.thumb || f.url}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                        {isInCollection && (
                          <div className="absolute inset-0 bg-blue-900/40 flex items-center justify-center">
                            <div className="bg-blue-600/90 rounded-lg px-2 py-1 text-[10px] text-white font-bold uppercase tracking-wide">
                              Добавлен
                            </div>
                          </div>
                        )}
                        {!isSelected && !isInCollection && (
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center border border-white/30">
                              <Icon name="plus" className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                            <Icon name="check" className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between p-4 border-t border-slate-700/50">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPickerPage((p) => Math.max(1, p - 1))}
                    disabled={pickerPage <= 1}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 transition-colors"
                  >
                    <Icon name="chevron-left" className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-400 font-medium">
                    {pickerPage} / {pickerTotalPages}
                  </span>
                  <button
                    onClick={() => setPickerPage((p) => Math.min(pickerTotalPages, p + 1))}
                    disabled={pickerPage >= pickerTotalPages}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 transition-colors"
                  >
                    <Icon name="chevron-right" className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={handlePickerAdd}
                  disabled={pickerSelected.size === 0}
                  className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all ${
                    pickerSelected.size > 0
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg'
                      : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  <Icon name="plus" className="w-4 h-4" />
                  Добавить{pickerSelected.size > 0 ? ` (${pickerSelected.size})` : ''}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Print Collection */}
        <PrintCollection
          prints={printCollection}
          selectedPrints={selectedPrintIds}
          onSelectPrint={onSelectPrintInCollection}
          onRemovePrint={onRemovePrintFromCollection}
          onUpdateArticle={onUpdateArticle}
          onUpdatePrintName={onUpdatePrintName}
          onSaveToCloud={onSaveCollectionToCloud}
          isSaving={isCloudSaving}
        />

        {/* Sidebar products */}
        <Sidebar
          products={currentTabProducts}
          onAddProduct={onAddProduct}
          onSaveConfig={handleSaveTabConfig}
          onExport={onExportZip}
          onSaveCloud={onSaveToCloud}
          isExporting={isExporting}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </div>

      {/* CENTER — Canvas area */}
      <div className="responsive-canvas-area flex-1 bg-slate-950 rounded-xl border border-slate-800 overflow-y-auto custom-scroll p-4 pb-8 space-y-4">
        {!selectedPrint ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-900 flex items-center justify-center border border-slate-800">
              <Icon name="image" className="w-8 h-8 opacity-50" />
            </div>
            <p>Выберите изображение слева, чтобы начать работу</p>
          </div>
        ) : currentTabProducts.filter((p) => p.enabled).length === 0 ? (
          <div className="h-80 flex flex-col items-center justify-center text-slate-500 gap-4">
            <div className="w-16 h-16 rounded-full bg-slate-900 flex items-center justify-center border border-slate-800">
              <Icon name="eye-off" className="w-8 h-8 opacity-50" />
            </div>
            <p>Все мокапы отключены</p>
            <p className="text-xs">Включите мокапы в списке слева галочкой</p>
          </div>
        ) : (
          <div
            className="responsive-canvas-grid grid gap-6"
            style={{ gridTemplateColumns: `repeat(${mockupsPerRow}, 1fr)` }}
          >
            {currentTabProducts
              .filter((p) => p.enabled)
              .map((product) => (
                <MockupGridItem
                  key={product.id}
                  product={product}
                  imageUrl={selectedPrint ? ('url' in selectedPrint ? selectedPrint.url : '') : ''}
                  transform={
                    currentTransforms[product.id] || { x: 0, y: 0, scale: 0.5, rotation: 0 }
                  }
                  onUpdateTransform={(newT: Transform) => updateTransform(product.id, newT)}
                  isActive={activeProductId === product.id}
                  onActivate={() => setActiveProductId(product.id)}
                  activeTab={activeTab}
                  maskColor={maskColor}
                />
              ))}
          </div>
        )}
      </div>

      {/* RIGHT SIDEBAR — Transform Panel */}
      <div className="responsive-sidebar w-full lg:w-80 bg-slate-900/50 rounded-xl border border-slate-800 shrink-0 lg:h-full overflow-y-auto custom-scroll pb-24">
        <TransformPanel
          transform={
            activeProductId
              ? currentTransforms[activeProductId] || { x: 0, y: 0, scale: 0.5, rotation: 0 }
              : { x: 0, y: 0, scale: 0.5, rotation: 0 }
          }
          onUpdateTransform={(newT) => activeProductId && updateTransform(activeProductId, newT)}
          dpi={activeProductId ? products.find((p) => p.id === activeProductId)?.dpi || 300 : 300}
          onDPIChange={(dpi) => activeProductId && onUpdateProductDPI(activeProductId, dpi)}
          activeProductId={activeProductId}
          isActive={!!activeProductId}
          mockupsPerRow={mockupsPerRow}
          setMockupsPerRow={setMockupsPerRow}
          presets={presets}
          onSavePreset={onSavePreset}
          onDeletePreset={onDeletePreset}
          onApplyPreset={onApplyPreset}
          saveStatus={saveStatus}
          maskColor={maskColor}
          setMaskColor={setMaskColor}
          onCopyTransform={copyTransformInternal}
          onPasteTransform={pasteTransformInternal}
        />
      </div>

      {/* Copy/Paste toast */}
      {copyToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-4 py-2 bg-slate-900/95 border border-slate-700/60 rounded-xl text-xs text-slate-200 shadow-xl backdrop-blur-sm flex items-center gap-2 animate-fade-in">
          <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          {copyToast}
        </div>
      )}
    </div>
  );
};
